import axios, { isAxiosError, type AxiosError, type AxiosInstance } from 'axios'
import { pushToast } from './toast'
import { i18n } from '@/locales'
import { fxErrorText } from '@/locales/features'

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api/v1'

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15000,
})

/** Shape of every error the DomainError handler emits.
 * The backend always sets `detail` and `code`; `extra` and `request_id`
 * are populated when available. */
export interface DomainErrorBody {
  detail: string
  code: string
  extra?: Record<string, unknown>
  request_id?: string
}

/** Type guard for axios errors that carry a DomainError body. */
export function isDomainError(e: unknown): e is AxiosError<DomainErrorBody> {
  if (!isAxiosError(e)) return false
  const body = e.response?.data as Partial<DomainErrorBody> | undefined
  return typeof body?.code === 'string' && typeof body?.detail === 'string'
}

/** Extract the `code` from a DomainError response, or '' for anything else. */
export function errorCode(e: unknown): string {
  return isDomainError(e) ? (e.response?.data?.code ?? '') : ''
}

/** Pull `extra.<key>` from a DomainError response, typed loosely. */
export function errorExtra<T = unknown>(e: unknown, key: string): T | undefined {
  if (!isDomainError(e)) return undefined
  const extra = e.response?.data?.extra
  return (extra && (extra as Record<string, unknown>)[key]) as T | undefined
}

/** `detail` string of any axios error body (also non-DomainError, e.g. 422).
 * Plan-rule codes get a localized text instead of the English detail. */
export function errorDetail(e: unknown): string | undefined {
  if (!isAxiosError(e)) return undefined
  const local = fxErrorText(String(i18n.global.locale.value), errorCode(e))
  if (local) return local
  const d = (e.response?.data as { detail?: unknown } | undefined)?.detail
  return typeof d === 'string' ? d : undefined
}

/** HTTP status of an axios error, else undefined. */
export function errorStatus(e: unknown): number | undefined {
  return isAxiosError(e) ? e.response?.status : undefined
}

/** `Retry-After` seconds from an axios error (NaN when absent). */
export function errorRetryAfter(e: unknown): number {
  return isAxiosError(e) ? Number(e.response?.headers?.['retry-after']) : NaN
}

/** `.message` of any thrown Error, else undefined. */
export function errorMessage(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined
}

/** Error toast with the server's `detail` (DomainError only) as message. */
export function toastError(e: unknown, title: string): void {
  pushToast({ kind: 'error', title, message: errorDetail(e) })
}

// Global 429 handler — surface a toast with the server's Retry-After
// countdown so users see why their action was blocked instead of a generic
// "failed". Individual callers can still catch + replace via their own UI
// (e.g. inline Resend-in-Ns button); the toast is a floor, not a ceiling.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 429) {
      const ra = Number(err.response.headers?.['retry-after'])
      const detail = errorDetail(err)
      pushToast({
        kind: 'warning',
        title: detail ?? i18n.global.t('errors.too_many_requests'),
        retryAfter: Number.isFinite(ra) && ra > 0 ? Math.min(ra, 3600) : 60,
      })
    }
    // ORG_ADMIN / SUPER_ADMIN without MFA: backend blocks everything but auth.
    if (err?.response?.status === 403 && errorCode(err) === 'mfa_enrollment_required') {
      void import('@/router').then(({ default: router }) => {
        if (router.currentRoute.value.name === 'profile') return
        pushToast({ kind: 'warning', title: i18n.global.t('errors.mfa_enrollment_required') })
        void router.push({ name: 'profile', query: { mfa: 'required' } })
      })
    }
    // Retired custom domain: continue on the platform host (same path).
    const movedTo = errorCode(err) === 'domain_moved' ? errorExtra<string>(err, 'redirect_base') : undefined
    if (typeof movedTo === 'string' && /^https?:\/\//.test(movedTo)) {
      window.location.replace(movedTo + window.location.pathname + window.location.search)
    }
    if (err?.response?.status === 403 && errorCode(err) === 'demo_read_only') {
      pushToast({ kind: 'info', title: i18n.global.t('demo.read_only') })
    }
    return Promise.reject(err)
  },
)

export interface CurrentOrganization {
  id: string
  name: string
  slug: string
  sms_enabled: boolean
  tier?: string
  trial_ends_at?: string | null
  read_only_reason?: 'subscription_past_due' | 'trial_expired' | null
  features?: Partial<Record<PlanFeature, boolean>>
  upgrade_url?: string | null
  /** Set only for one-click demo orgs (ISO time the sandbox is purged). */
  demo_expires_at?: string | null
}

export type PlanFeature = 'sms' | 'custom_domain' | 'webhooks' | 'api_keys' | 'sso' | 'embedded'

export interface CurrentUser {
  id: string
  email: string
  name: string
  global_role: 'SUPER_ADMIN' | 'USER'
  email_verified?: boolean
  org_role: 'ORG_ADMIN' | 'ORG_MEMBER'
  organization: CurrentOrganization
  is_impersonating: boolean
  // Terms/DPA version bump → one-time accept modal.
  legal_acceptance_required?: boolean
  dpa_acceptance_required?: boolean
  terms_version?: string
  dpa_version?: string
  // Role needs a (stronger) second factor: only the profile page works.
  mfa_enrollment_required?: boolean
}

export interface Team {
  id: string
  name: string
  slug: string
  members_count: number
  created_at: string
}

export interface TemplateRead {
  id: string
  name: string
  description: string
  category: string
  uses_count: number
  created_at: string
  template_type?: 'PRIVATE' | 'TEAM_SHARED' | 'ORG_SHARED' | 'PUBLIC_LINK'
}

export interface OrgBranding {
  logo_url: string
  primary_color: string
  theme?: string
  from_name: string
  email_footer: string
  default_verification_type: VerificationType
  default_language?: string
  allowed_signature_types?: string[]
  include_audit_certificate?: boolean
  email_notifications?: Record<string, boolean>
}

export interface OrganizationRead {
  id: string
  name: string
  slug: string
  email_provider: 'MANAGED' | 'SMTP' | 'RESEND_BYOA' | 'MICROSOFT_GRAPH' | 'AWS_SES' | 'MAILGUN'
  email_configured: boolean
  email_state?: 'managed' | 'custom' | 'incomplete'
  branding: OrgBranding
  wezel_podpisu_enabled: boolean
  wezel_podpisu_platform_enabled: boolean
  wezel_podpisu_available: boolean
}

export interface DocumentSummary {
  id: string
  title: string
  status: 'DRAFT' | 'PENDING' | 'COMPLETED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED'
  recipients_count: number
  created_at: string
  sent_at: string | null
  completed_at: string | null
}

export type SigningMethod =
  | 'INTERNAL_DRAWN'
  | 'WP_PZ'
  | 'WP_PZ_SIE'
  | 'WP_PS'
  | 'WP_PQ'
  | 'WP_MO'
  | 'QES'

export interface RecipientRead {
  id: string
  email: string
  name: string
  role: 'SIGNER' | 'APPROVER' | 'VIEWER' | 'CC'
  status: 'NOT_SENT' | 'SENT' | 'VIEWED' | 'SIGNED' | 'REJECTED' | 'EXPIRED' | 'BOUNCED'
  signed_at: string | null
  signing_order_index?: number
  signing_method?: SigningMethod
  signing_url?: string | null
  identity_verification?: RecipientIdentityVerification | null
  rejection_reason?: string | null
}

export interface RecipientIdentityVerification {
  status: 'PENDING' | 'IN_REVIEW' | 'VERIFIED' | 'MISMATCH' | 'FAILED'
  provider: string
  provider_label: string
  updated_at: string
}

export interface WezelPodpisuStartResponse {
  redirect_url: string
  ext_sign_request_id: string
  signing_method: SigningMethod
  expires_at: string | null
}

export interface WezelPodpisuStatus {
  status: string
  signing_method: SigningMethod
  signer_given_name: string | null
  signer_surname: string | null
  fault_code: string | null
  fault_message: string | null
}

export interface FieldConditional {
  field_id: string
  equals: string
}

export type FieldType =
  | 'SIGNATURE'
  | 'INITIALS'
  | 'TEXT'
  | 'DATE'
  | 'NUMBER'
  | 'EMAIL'
  | 'CHECKBOX'
  | 'DROPDOWN'
  | 'COMPANY'

export interface FieldRead {
  id: string
  type: FieldType
  page: number
  position_x: number
  position_y: number
  width: number
  height: number
  recipient_id: string | null
  required: boolean
  label: string | null
  options: string[]
  show_if?: FieldConditional | null
}

export interface DocumentDetail {
  id: string
  title: string
  status: DocumentSummary['status']
  signing_order: 'PARALLEL' | 'SEQUENTIAL'
  created_at: string
  sent_at: string | null
  completed_at: string | null
  recipients: RecipientRead[]
  verification_type?: VerificationType
  fields: FieldRead[]
  has_signed_pdf: boolean
  expires_at?: string | null
  allow_delegation?: boolean
  cancel_reason?: string | null
  void_reason_public?: boolean
  redirect_url_after_sign?: string | null
  cancelled_at?: string | null
  legal_hold?: boolean
  legal_hold_reason?: string | null
  legal_hold_at?: string | null
  /** Only on the cancel response when a correction copy was made. */
  copy_document_id?: string | null
}

export type VerificationType = 'NONE' | 'EMAIL_OTP' | 'SMS_OTP' | 'BOTH_OTP' | 'ID_VERIFIED'

export interface SigningBranding {
  logo_url: string | null
  brand_name: string
  primary_color: string
  theme?: string
}

export interface SigningPageData {
  document_id: string
  document_title: string
  document_status: DocumentSummary['status']
  organization_name: string
  sender_name?: string
  is_demo?: boolean
  branding?: SigningBranding | null
  verification_type: VerificationType
  otp_verified: boolean
  sms_otp_verified?: boolean
  id_verified?: boolean
  allow_delegation?: boolean
  recipient: RecipientRead
  all_recipients: RecipientRead[]
  fields: FieldRead[]
  intake_status?: 'pending' | 'waiting' | null
  redirect_host?: string | null
  redirect_after_sign?: string | null
  embed_origins?: string[]
}

export interface AuditEntry {
  id: string
  event_type: string
  actor_email: string | null
  actor_ip: string | null
  target_type: string | null
  created_at: string
  metadata: Record<string, unknown>
}

export interface SignatureInfo {
  id: string
  recipient_id: string
  recipient_name: string
  signed_at: string
  ip_address: string | null
  user_agent: string | null
  evidence_hash: string
}

export interface TeamMember {
  id: string
  user_id: string
  email: string
  name: string
  role: 'TEAM_ADMIN' | 'TEAM_MANAGER' | 'TEAM_MEMBER' | 'TEAM_VIEWER'
  joined_at: string
}
